import { getCurrentApiHost, API_NAMESPACE, API_VERSION, NITRO_OAUTH_HOST } from 'config/environment';
const oauthState = {};
const generateRandomString = (length)=>{
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);
    return Array.from(randomValues, (byte)=>characters.charAt(byte % charactersLength)).join('');
};
function base64UrlEncode(buffer) {
    const arr = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    return btoa(String.fromCharCode(...arr)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
async function sha256Digest(input) {
    const encoder = new TextEncoder();
    const _data = encoder.encode(input);
    return crypto.subtle.digest('SHA-256', _data);
}
export const redirectToOAuth = async (clientId)=>{
    try {
        const codeVerifier = generateRandomString(43);
        const codeChallenge = base64UrlEncode(await sha256Digest(codeVerifier));
        const randomString = generateRandomString(32);
        const state = base64UrlEncode(new TextEncoder().encode(randomString));
        oauthState.code_verifier = codeVerifier;
        oauthState.state = state;
        oauthState.code_challenge = codeChallenge;
        // Mirror to localStorage — Safari ITP/incognito can clear sessionStorage on the auth hop.
        sessionStorage.setItem('codeVerifier', codeVerifier);
        try {
            localStorage.setItem('codeVerifier', codeVerifier);
        } catch (unused) {
        // Best-effort mirror — don't abort the OAuth redirect if localStorage is blocked.
        }
        const currentHost = window.location.host;
        const currentScheme = window.location.protocol.replace(':', '');
        if (!clientId) {
            return;
        }
        const parameters = new URLSearchParams({
            clientId,
            state,
            codeChallenge,
            codeChallengeMethod: 'S256',
            redirectUri: `${currentScheme}://${currentHost}/implicit/callback`
        });
        const redirectUrl = `${NITRO_OAUTH_HOST}/${clientId}/login?${parameters.toString()}`;
        window.location.href = redirectUrl;
    } catch (unused) {
        window.location.href = '/login';
    }
};
// Like redirectToOAuth, but appends the magic token so id calls /authenticate/magic-link
// directly, and stashes it in sessionStorage for LoginCallback to blacklist after login.
export const redirectToOAuthWithMagicLink = async (clientId, magicToken)=>{
    try {
        const codeVerifier = generateRandomString(43);
        const codeChallenge = base64UrlEncode(await sha256Digest(codeVerifier));
        const randomString = generateRandomString(32);
        const state = base64UrlEncode(new TextEncoder().encode(randomString));
        // Stash the token before the best-effort localStorage mirror, which can throw in
        // Safari private mode and would otherwise drop the magic-link action.
        sessionStorage.setItem('codeVerifier', codeVerifier);
        sessionStorage.setItem('linkMagicToken', magicToken);
        try {
            localStorage.setItem('codeVerifier', codeVerifier);
        } catch (unused) {
        // best-effort mirror
        }
        const currentHost = window.location.host;
        const currentScheme = window.location.protocol.replace(':', '');
        if (!clientId) {
            return;
        }
        const parameters = new URLSearchParams({
            clientId,
            state,
            codeChallenge,
            codeChallengeMethod: 'S256',
            redirectUri: `${currentScheme}://${currentHost}/implicit/callback`,
            token: magicToken
        });
        const redirectUrl = `${NITRO_OAUTH_HOST}/${clientId}/login?${parameters.toString()}`;
        window.location.href = redirectUrl;
    } catch (_error) {
        window.location.href = '/login';
    }
};
export const exchangeCodeWithId = async (code, redirectUri, codeVerifier, clientId)=>{
    const response = await fetch(`${NITRO_OAUTH_HOST}/${API_NAMESPACE}/${API_VERSION}/token`, {
        method: 'POST',
        headers: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            code,
            redirectUri,
            codeVerifier,
            clientId
        })
    });
    if (!response.ok) {
        throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
};
// Converts the id-issued SHORT_LIVED into an rl_session cookie via rocket-api. Optional
// LINK_MAGIC rides as X-Magic-Token purely to dispatch its link-type action (already
// validated/blacklisted by id at PKCE exchange) — only SHORT_LIVED failures return an error.
export const authenticateWithId = async (accessToken, magicToken)=>{
    const headers = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: `Bearer ${accessToken}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/json'
    };
    if (magicToken) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        headers['X-Magic-Token'] = magicToken;
    }
    const response = await fetch(`${getCurrentApiHost()}/${API_NAMESPACE}/${API_VERSION}/authentication/token`, {
        method: 'POST',
        headers
    });
    if (!response.ok) {
        var _ref;
        let body;
        try {
            body = await response.json();
        } catch (unused) {
        // body wasn't JSON — fall through to generic message
        }
        const message = (_ref = body === null || body === void 0 ? void 0 : body.message) !== null && _ref !== void 0 ? _ref : `Authentication failed: ${response.status} ${response.statusText}`;
        const error = new Error(message);
        error.stage = body === null || body === void 0 ? void 0 : body.error;
        throw error;
    }
};
